/**
 * Reading a routing policy the way the backend reads it.
 *
 * The one thing a screen for this has to get across, because it is the
 * opposite of what the widget looks like: **an empty allowlist allows
 * everything.** Unticking every provider does not lock the machine down, it
 * removes the restriction. Someone who believes otherwise thinks they are
 * locked down when they are wide open, and that is the worse direction to be
 * wrong in.
 *
 * The rest of this file is about the other trap: allowing a provider and none
 * of its models. The backend refuses that at the moment a message is sent,
 * with a clear message, but by then the user has already typed one.
 */

/** Does this policy actually restrict anything? */
export function isRestricted(policy) {
  return Boolean(
    (policy?.allowed_providers || []).length || (policy?.allowed_models || []).length
  );
}

/**
 * Providers that are allowed but have nothing to answer with.
 *
 * @param policy   {{allowed_providers: string[], allowed_models: string[]}}
 * @param catalog  {{[provider: string]: string[]}} the models each provider offers
 */
export function providersWithNoAllowedModel(policy, catalog) {
  const providers = policy?.allowed_providers || [];
  const models = policy?.allowed_models || [];
  // No model restriction means every provider keeps all of its models.
  if (!models.length) return [];

  return providers.filter((provider) => {
    const offered = catalog[provider] || [];
    return !offered.some((model) => models.includes(model));
  });
}

/**
 * Models that are allowed but belong to no allowed provider, so nothing can
 * ever reach them. Harmless, and confusing to look at.
 */
export function modelsNoProviderCanServe(policy, catalog) {
  const providers = policy?.allowed_providers || [];
  const models = policy?.allowed_models || [];
  if (!providers.length || !models.length) return [];

  const reachable = new Set(providers.flatMap((p) => catalog[p] || []));
  return models.filter((model) => !reachable.has(model));
}

/**
 * Everything worth telling the user, as plain sentences, worst first.
 * An empty array means the policy is coherent.
 */
export function policyWarnings(policy, catalog) {
  const warnings = [];

  for (const provider of providersWithNoAllowedModel(policy, catalog)) {
    warnings.push({
      level: "error",
      text:
        `${provider} is allowed but none of its models are. Sending a message ` +
        `that lands on ${provider} will be refused until you allow one of its ` +
        `models, or stop allowing ${provider}.`,
    });
  }

  const stranded = modelsNoProviderCanServe(policy, catalog);
  if (stranded.length) {
    warnings.push({
      level: "warning",
      text:
        `${stranded.join(", ")} ${stranded.length === 1 ? "is" : "are"} allowed, ` +
        `but no allowed provider offers ${stranded.length === 1 ? "it" : "them"}. ` +
        `This has no effect.`,
    });
  }

  return warnings;
}
